import React, { useState, useContext, useRef } from "react";
import { NavLink } from "react-router-dom";
import { useReactToPrint } from "react-to-print";

//pages & includes
import ManageSidebar from "../ManageSidebar";

//functions
import {
  _t,
  getCookie,
  pagination,
  paginationLoading,
  showingData,
  searchedShowingData,
} from "../../../../../functions/Functions";
import { useTranslation } from "react-i18next";

//axios and base url
import axios from "axios";
import { BASE_URL } from "../../../../../BaseUrl";

//3rd party packages
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Moment from "react-moment";

//context consumer
import { SettingsContext } from "../../../../../contexts/Settings";
import { UserContext } from "../../../../../contexts/User";
import IngredientStockPrint from "./IngredientStockPrint";
import CustomSpinLoader from "../../../../components/CustomSpinLoader";

const StockConsumptionList = () => {
  const { t } = useTranslation();
  //getting context values here
  let {
    //common
    loading,
    setLoading,
  } = useContext(SettingsContext);

  let {
    //wastage
    stockConsumptionList,
    setPaginatedStockConsumption,
    stockConsumptionForSearch,
    setStockConsumptionList,
    setStockConsumptionForSearch,

    //pagination
    dataPaginating,
    setDataPaginating
  } = useContext(UserContext);

  let common_toast = {
    position: "bottom-center",
    autoClose: 10000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    className: "text-center toast-notification",
  };

  //search result
  let [searchedConsumption, setSearchedConsumption] = useState({
    list: null,
    searched: false,
  });

  //search purchase here
  const handleSearch = (e) => {
    let searchInput = e.target.value.toLowerCase();
    if (searchInput.length === 0) {
      setSearchedConsumption({ ...searchedConsumption, searched: false });
    } else {
      let searchedList = stockConsumptionForSearch.filter((item) => {
        //name
        let lowerCaseDocNo = item.document_identity?.toLowerCase();

        //email
        let lowerCaseItemRemarks = item.remarks?.toLowerCase();
        let lowerCaseBranch = item.branch_name?.toLowerCase();
        return (
          (lowerCaseItemRemarks &&
            lowerCaseItemRemarks.includes(searchInput)) ||
          (lowerCaseDocNo && lowerCaseDocNo.includes(searchInput)) ||
          (lowerCaseBranch && lowerCaseBranch.includes(searchInput))
        );
      });
      setSearchedConsumption({
        ...searchedConsumption,
        list: searchedList,
        searched: true,
      });
    }
  };

  // print bills states and function
  const [newRow, setNewRow] = useState([]);
  const [documentDate, setDocumentDate] = useState(new Date());
  let [newConsumption, setNewConsumption] = useState({
    branch: null,
    remarks: "",
    document_identity: "",
  });
  const componentRef = useRef();
  const handleOnlyPrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const handleGetPrintData = async (id) => {
    const url = BASE_URL + "/settings/consumption/" + parseInt(id);
    try {
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      if (res.data) {
        const date = new Date(res.data?.document_date);
        setNewConsumption({
          documentDate: date,
          branch: {
            id: res.data.branch_id,
            name: res.data.branch_name,
          },
          remarks: res.data?.remarks,
          document_identity: res.data?.document_identity,
        });
        setNewRow([...res.data?.consumption_details]);
        Promise.resolve().then(() => {
          handleOnlyPrint();
        });
      }
    } catch (error) {
      console.log("error==>", error);
    }
  };

  const handlePostApi = async (id) => {
    setDataPaginating(true);
    const url = BASE_URL + "/settings/consumption-post";
    let formData = new FormData();
    formData.append("id", id);
    formData.append("status", 1);
    try {
      const res = await axios.post(url, formData, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      if (res.data) {
        setStockConsumptionList(res.data[0]);
        setStockConsumptionForSearch(res.data[1]);
        setSearchedConsumption({
          ...searchedConsumption,
          list: res.data[1]
        })
        toast.success(`${_t(t("Stock consumption has been post"))}`, {
          position: "bottom-center",
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          className: "text-center toast-notification",
        });
      }
      setDataPaginating(false);
    } catch (err) {
      setDataPaginating(false);
      if(err.response?.data?.status){
        toast.error(<span style={{whiteSpace:"pre-line"}}>{err.response.data?.message}</span>, {
          position: "bottom-center",
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          className: "text-center toast-notification",
        });
        return
      }
      toast.error(`${_t(t("Please try again"))}`, {
        position: "bottom-center",
        autoClose: 10000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        className: "text-center toast-notification",
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>{_t(t("Stock Consumption List"))}</title>
      </Helmet>

      {/* main body */}
      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ManageSidebar />
            </div>
            {/* left Sidebar ends */}

            {/* Rightbar contents */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                <div className="fk-scroll--pos-table-menu" data-simplebar>
                  <div className="t-pl-15 t-pr-15">
                    {/* Loading effect */}
                    <div key="fragment3">
                      {/* next page data spin loading */}
                      <CustomSpinLoader loading={loading || dataPaginating}/>
                      {/* spin loading ends */}

                      <div className="row gx-2 align-items-center t-pt-15 t-pb-15">
                        <div className="col-md-6 col-lg-5 t-mb-15 mb-md-0">
                          <ul className="t-list fk-breadcrumb">
                            <li className="fk-breadcrumb__list">
                              <span className="t-link fk-breadcrumb__link text-capitalize">
                                {!searchedConsumption.searched
                                  ? _t(t("Stock Consumption List"))
                                  : _t(t("Search Result"))}
                              </span>
                            </li>
                          </ul>
                        </div>
                        <div className="col-md-6 col-lg-7">
                          <div className="row gx-3 align-items-center">
                            {/* Search customer */}
                            <div className="col-md-9 t-mb-15 mb-md-0">
                              <div className="input-group">
                                <div className="form-file">
                                  <input
                                    type="text"
                                    className="form-control border-0 form-control--light-1 rounded-left"
                                    placeholder={_t(t("Search")) + ".."}
                                    onChange={handleSearch}
                                  />
                                </div>
                                <button
                                  className="btn btn-primary rounded-right"
                                  type="button"
                                >
                                  <i
                                    className="fa fa-search"
                                    aria-hidden="true"
                                  ></i>
                                </button>
                              </div>
                            </div>

                            {/* Add customer modal trigger button */}
                            <div className="col-md-3 text-md-right">
                              <NavLink
                                to="/dashboard/manage/stock/stock-consumption-add"
                                className="btn btn-primary xsm-text text-uppercase btn-lg btn-block rounded"
                              >
                                {_t(t("add new"))}
                              </NavLink>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Table */}
                      <div className="">
                        <table className="table table-bordered table-hover min-table-height">
                          <thead className="align-middle">
                            <tr>
                              <th
                                scope="col"
                                className="sm-text text-capitalize align-middle text-center border-1 border"
                              >
                                {_t(t("S/L"))}
                              </th>

                              <th
                                scope="col"
                                className="sm-text text-capitalize align-middle text-center border-1 border"
                              >
                                {_t(t("Document No"))}
                              </th>

                              <th
                                scope="col"
                                className="sm-text text-capitalize align-middle text-center border-1 border"
                              >
                                {_t(t("Document Date"))}
                              </th>

                              <th
                                scope="col"
                                className="sm-text text-capitalize align-middle text-center border-1 border"
                              >
                                {_t(t("Branch Name"))}
                              </th>

                              <th
                                scope="col"
                                className="sm-text text-capitalize align-middle text-center border-1 border"
                              >
                                {_t(t("Remarks"))}
                              </th>

                              <th
                                scope="col"
                                className="sm-text text-capitalize align-middle text-center border-1 border"
                              >
                                {_t(t("Action"))}
                              </th>
                            </tr>
                          </thead>
                          <tbody className="align-middle">
                            {/* loop here, logic === !search && haveData && haveDataLegnth > 0*/}
                            {!searchedConsumption.searched
                              ? [
                                  stockConsumptionList && [
                                    stockConsumptionList?.data?.length === 0 ? (
                                      <tr className="align-middle">
                                        <td
                                          scope="row"
                                          colSpan="7"
                                          className="xsm-text align-middle text-center"
                                        >
                                          {_t(t("No data available"))}
                                        </td>
                                      </tr>
                                    ) : (
                                      stockConsumptionList?.data &&
                                      stockConsumptionList.data.map((item, index) => {
                                        return (
                                          <tr
                                            className="align-middle"
                                            key={index}
                                          >
                                            <th
                                              scope="row"
                                              className="xsm-text text-capitalize align-middle text-center"
                                            >
                                              {index +
                                                1 +
                                                (stockConsumptionList.current_page -
                                                  1) *
                                                  stockConsumptionList.per_page}
                                            </th>

                                            <td className="xsm-text text-capitalize align-middle text-center">
                                              {item.document_identity}
                                            </td>

                                            <td className="xsm-text align-middle text-center">
                                              <Moment format="DD-MMMM-YYYY">
                                                {item.document_date}
                                              </Moment>
                                            </td>

                                            <td className="xsm-text align-middle text-center">
                                              {item.branch_name}
                                            </td>

                                            <td className="xsm-text text-capitalize align-middle text-center">
                                              {item.remarks}
                                            </td>

                                            <td className="xsm-text text-capitalize align-middle text-center">
                                              <div className="d-flex justify-content-center">
                                                {item.is_post !== 1 ? (
                                                  <>
                                                    <NavLink
                                                      className="btn btn-primary ml-2 rounded-sm"
                                                      to={`/dashboard/manage/stock/stock-consumption-edit/${item.id}`}
                                                      style={{
                                                        height: "1.7rem",
                                                        width: "1.7rem",
                                                      }}
                                                    >
                                                      <div className="d-flex align-items-center justify-content-center">
                                                        <i
                                                          className="fa fa-pencil edit-icon"
                                                          aria-hidden="true"
                                                        ></i>
                                                      </div>
                                                    </NavLink>
                                                    <button
                                                      className="btn btn-dark ml-2 rounded-sm del-btn"
                                                      title="Post consumption"
                                                      style={{
                                                        padding:"1px 0px 0px 1px",
                                                      }}
                                                      disabled={loading}
                                                      onClick={() =>
                                                        handlePostApi(item.id)
                                                      }
                                                    >
                                                      <i
                                                        className="fa fa-check-square-o"
                                                        aria-hidden="true"
                                                      ></i>
                                                    </button>
                                                  </>
                                                ) : null}
                                                <button
                                                  className="btn btn-secondary ml-2 rounded-sm del-btn"
                                                  onClick={() =>
                                                    handleGetPrintData(item.id)
                                                  }
                                                >
                                                  <i className="fa fa-print edit-icon"></i>
                                                </button>
                                              </div>
                                            </td>
                                          </tr>
                                        );
                                      })
                                    ),
                                  ],
                                ]
                              : [
                                  /* searched data, logic === haveData*/
                                  searchedConsumption && [
                                    searchedConsumption.list.length === 0 ? (
                                      <tr className="align-middle">
                                        <td
                                          scope="row"
                                          colSpan="7"
                                          className="xsm-text align-middle text-center"
                                        >
                                          {_t(t("No data available"))}
                                        </td>
                                      </tr>
                                    ) : (
                                      searchedConsumption.list.map(
                                        (item, index) => {
                                          return (
                                            <tr
                                              className="align-middle"
                                              key={index}
                                            >
                                              <th
                                                scope="row"
                                                className="xsm-text text-capitalize align-middle text-center"
                                              >
                                                {index +
                                                  1 +
                                                  (stockConsumptionList.current_page -
                                                    1) *
                                                    stockConsumptionList.per_page}
                                              </th>

                                              <td className="xsm-text text-capitalize align-middle text-center">
                                                {item.document_identity}
                                              </td>

                                              <td className="xsm-text align-middle text-center">
                                                <Moment format="DD-MMMM-YYYY">
                                                  {item.document_date}
                                                </Moment>
                                              </td>

                                              <td className="xsm-text align-middle text-center">
                                                {item.branch_name}
                                              </td>

                                              <td className="xsm-text text-capitalize align-middle text-center">
                                                {item?.remarks}
                                              </td>

                                              <td className="xsm-text text-capitalize align-middle text-center">
                                                <div className="d-flex justify-content-center">
                                                  {item.is_post !== 1 ? (
                                                    <>
                                                      <NavLink
                                                        className="btn btn-primary ml-2 rounded-sm"
                                                        to={`/dashboard/manage/stock/stock-consumption-edit/${item.id}`}
                                                        style={{
                                                          height: "1.7rem",
                                                          width: "1.7rem",
                                                        }}
                                                      >
                                                        <div className="d-flex align-items-center justify-content-center">
                                                          <i
                                                            className="fa fa-pencil edit-icon"
                                                            aria-hidden="true"
                                                          ></i>
                                                        </div>
                                                      </NavLink>
                                                      <button
                                                        className="btn btn-dark ml-2 rounded-sm del-btn"
                                                        title="Post consumption"
                                                        style={{
                                                          padding: "1px 0px 0px 1px",
                                                        }}
                                                        disabled={loading}
                                                        onClick={() =>
                                                          handlePostApi(item.id)
                                                        }
                                                      >
                                                        <i
                                                          className="fa fa-check-square-o"
                                                          aria-hidden="true"
                                                        ></i>
                                                      </button>
                                                    </>
                                                  ) : null}
                                                  <button
                                                    className="btn btn-secondary ml-2 rounded-sm del-btn"
                                                    onClick={() =>
                                                      handleGetPrintData(
                                                        item.id
                                                      )
                                                    }
                                                  >
                                                    <i className="fa fa-print edit-icon"></i>
                                                  </button>
                                                </div>
                                              </td>
                                            </tr>
                                          );
                                        }
                                      )
                                    ),
                                  ],
                                ]}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* pagination loading effect */}
              {loading === true
                ? paginationLoading()
                : [
                    // logic === !searched
                    !searchedConsumption.searched ? (
                      <div key="fragment4">
                        <div className="t-bg-white mt-1 t-pt-5 t-pb-5">
                          <div className="row align-items-center t-pl-15 t-pr-15">
                            <div className="col-md-7 t-mb-15 mb-md-0">
                              {/* pagination function */}
                              {pagination(
                                stockConsumptionList,
                                setPaginatedStockConsumption
                              )}
                            </div>
                            <div className="col-md-5">
                              <ul className="t-list d-flex justify-content-md-end align-items-center">
                                <li className="t-list__item">
                                  <span className="d-inline-block sm-text">
                                    {showingData(stockConsumptionList)}
                                  </span>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // if searched
                      <div className="t-bg-white mt-1 t-pt-5 t-pb-5">
                        <div className="row align-items-center t-pl-15 t-pr-15">
                          <div className="col-md-7 t-mb-15 mb-md-0">
                            <ul className="t-list d-flex">
                              <li className="t-list__item no-pagination-style">
                                <button
                                  className="btn btn-primary btn-sm"
                                  onClick={() =>
                                    setSearchedConsumption({
                                      ...searchedConsumption,
                                      searched: false,
                                    })
                                  }
                                >
                                  {_t(t("Clear Search"))}
                                </button>
                              </li>
                            </ul>
                          </div>
                          <div className="col-md-5">
                            <ul className="t-list d-flex justify-content-md-end align-items-center">
                              <li className="t-list__item">
                                <span className="d-inline-block sm-text">
                                  {searchedShowingData(
                                    searchedConsumption,
                                    stockConsumptionForSearch
                                  )}
                                </span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    ),
                  ]}
            </div>
            {/* Rightbar contents end*/}
          </div>
        </div>
      </main>
      {/* main body ends */}
      <IngredientStockPrint
        name="Stock Consumption"
        documentDate={documentDate}
        newConsumption={newConsumption}
        newRow={newRow}
        print={componentRef}
      />
    </>
  );
};

export default StockConsumptionList;
